/*
 * Shot-type definitions shared by every shot surface. `key` matches the
 * backend `shot_outcomes` map keys; `lbl` resolves to a
 * `tournament:label.*` translation.
 */
import type { PlayerStatistics } from '@/types/statistics';
import { SHOW_WINNERS } from '@/lib/constants';

export type ShotEntry = NonNullable<
  PlayerStatistics['shots_tracking']
>['shot_outcomes'][string][number];

// Serves are not rally shots — the previous report excluded them from every
// shot count/plot ("drop Serviço tab"), so we drop both backend serve aliases.
const SERVE_KEYS = new Set(['serve', 'rally_server']);

// Landing types that end the point against the striker.
const ERROR_OUTCOMES = new Set(['net', 'wall_bounce']);

// Overhead and smash used to share one chip. They are separate classes in the
// pipeline (config.yaml lists both under offensive_shots) and a smash is a
// different shot to read than a bandeja, so each now has its own axis, chip
// and speed row — every backend stroke key maps to exactly one chip.

export type ShotGroup = 'offensive' | 'defensive';

export interface ShotTypeDefinition {
  key: string;
  lbl: string;
  group: ShotGroup;
  backendKeys: string[];
}

/**
 * The eight real shot axes, in the single order used by every radar.
 *
 * Ordered so the four offensive axes sit contiguously around the top of the
 * radar and the four defensive ones around the bottom — the shape then reads
 * as attack-vs-defence before any label is read.
 */
export const SHOT_TYPE_CATALOG: ShotTypeDefinition[] = [
  {
    key: 'overhead',
    lbl: 'overhead',
    group: 'offensive',
    backendKeys: ['overhead'],
  },
  {
    key: 'smash',
    lbl: 'smash',
    group: 'offensive',
    backendKeys: ['smash'],
  },
  {
    key: 'forehand_volley',
    lbl: 'fhVolley',
    group: 'offensive',
    backendKeys: ['forehand_volley'],
  },
  {
    key: 'forehand',
    lbl: 'forehand',
    group: 'defensive',
    backendKeys: ['forehand'],
  },
  {
    key: 'forehand_lob',
    lbl: 'fhLob',
    group: 'defensive',
    backendKeys: ['forehand_lob'],
  },
  {
    key: 'backhand_lob',
    lbl: 'bhLob',
    group: 'defensive',
    backendKeys: ['backhand_lob'],
  },
  {
    key: 'backhand',
    lbl: 'backhand',
    group: 'defensive',
    backendKeys: ['backhand'],
  },
  {
    key: 'backhand_volley',
    lbl: 'bhVolley',
    group: 'offensive',
    backendKeys: ['backhand_volley'],
  },
];

/** The synthetic All filter followed by the eight canonical shot filters. */
export const SHOT_TYPES: { key: string; lbl: string }[] = [
  { key: 'all', lbl: 'all' },
  ...SHOT_TYPE_CATALOG.map(({ key, lbl }) => ({ key, lbl })),
];

export const SHOT_RADAR_AXES = SHOT_TYPE_CATALOG;

/** A serve alias — excluded from every shot count/plot. */
export const isServeType = (key: string): boolean => SERVE_KEYS.has(key);

/** The chip a backend shot type belongs to — now one-to-one. */
export const chipForType = (key: string): string => key;

/**
 * The backend shot-type keys a chip aggregates — the inverse of chipForType.
 * One key per chip today; kept as a list so a future chip can pool several
 * without every call site changing. Used to read the per-zone `by_shot_type`
 * breakdown by chip.
 */
export const backendKeysForChip = (chip: string): string[] =>
  SHOT_TYPE_CATALOG.find((shotType) => shotType.key === chip)?.backendKeys ?? [
    chip,
  ];

/**
 * Shots of `type` ('all' = every rally type) that the pipeline accepted as
 * reliable.
 *
 * The backend tags every detection with `accepted`; low-confidence ones are
 * `accepted: false`. We drop those EVERYWHERE shots are counted or drawn — the
 * same filter the main report applies (see transforms.ts) — so the tournament
 * shows the same trustworthy shot volume rather than the raw detection count.
 * Serves are excluded entirely.
 */
export function selectShots(ps: PlayerStatistics, type: string): ShotEntry[] {
  const so = ps.shots_tracking?.shot_outcomes;
  if (!so) return [];
  if (isServeType(type)) return [];
  const pick =
    type === 'all'
      ? Object.entries(so)
          .filter(([k]) => !SERVE_KEYS.has(k))
          .flatMap(([, arr]) => arr)
      : backendKeysForChip(type).flatMap((k) => so[k] ?? []);
  return pick.filter((s) => s.accepted);
}

/**
 * The bucket a shot falls into — the ONE classifier behind the ball map's
 * markers, its legend tallies and the consistency rates, so the three can never
 * disagree about what a shot was.
 *
 * Priority matters and the buckets are disjoint: an error outranks everything
 * (the point ended badly), then a winner (it ended well); what's left splits on
 * whether the ball bounced before the opponent reached it.
 *
 * An error is read from `outcome.type` OR the `is_error` flag, because neither
 * alone is sufficient: the flag is `.optional()` in the schema, so a payload
 * that drops it would otherwise report a 0% error rate, while the flag catches
 * shots whose landing type alone doesn't tell the story (a serve that bounces
 * outside the box is a fault, not a floor bounce).
 */
export function classifyShot(s: ShotEntry): string {
  const landing = s.outcome.type ?? '';
  if (s.is_error === true || ERROR_OUTCOMES.has(landing)) return 'error';
  if (SHOW_WINNERS && s.is_winner) return 'winner';
  if (landing === 'floor_bounce') return 'bounce';
  return 'interception';
}

export interface ShotConsistency {
  inRate: number;
  errorRate: number;
  /** Share of the accepted shots that ended the rally as winners. */
  winnerRate: number;
}

/**
 * Outcome rates over a shot list, bucketed exactly as the ball map draws it.
 * Null for an empty list — no shots means no rates, not 0%.
 *
 * In-play is everything that isn't an error, so the pair sums to 1; winners are
 * a subset of in-play (a winner landed), which is why they can't be a third
 * channel on the diverging bar.
 */
export function shotRates(shots: ShotEntry[]): ShotConsistency | null {
  if (shots.length === 0) return null;
  let errors = 0;
  let winners = 0;
  for (const s of shots) {
    const cat = classifyShot(s);
    if (cat === 'error') errors++;
    else if (cat === 'winner') winners++;
  }
  return {
    inRate: (shots.length - errors) / shots.length,
    errorRate: errors / shots.length,
    winnerRate: winners / shots.length,
  };
}

/**
 * Consistency rates for a shot chip, computed from the accepted rally shots
 * themselves rather than the backend's `performance` map.
 *
 * The published rates cover a different population: the backend pools both
 * serve aliases into `global`, so a player with 164 rally shots is scored over
 * 208, and service winners inflate the winner rate against an error count the
 * serves never contribute to. Deriving from `selectShots` puts every number on
 * screen — markers, legend tallies, rates, ratios — on one population, so the
 * ratio can be verified by counting dots on the court.
 */
export function selectShotConsistency(
  ps: PlayerStatistics,
  type: string,
): ShotConsistency | null {
  return shotRates(selectShots(ps, type));
}
